import React, { useState, useEffect, useMemo } from 'react';
import './home.styles.scss';

const HomeNew = () => {
  const [currentService, setCurrentService] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [typewriterText, setTypewriterText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [textIndex, setTextIndex] = useState(0);

  const services = useMemo(() => [
    {
      icon: '🗄️',
      title: 'Database Administration',
      description: 'Complete remote database management and administration services'
    },
    {
      icon: '�',
      title: 'MySQL Management',
      description: 'Expert MySQL database administration and optimization'
    },
    {
      icon: '🐘',
      title: 'PostgreSQL Services',
      description: 'Professional PostgreSQL database management and support'
    },
    {
      icon: '🏢',
      title: 'SQL Server DBA',
      description: 'Comprehensive Microsoft SQL Server administration'
    },
    {
      icon: '🏛️',
      title: 'Oracle Database',
      description: 'Enterprise Oracle database administration services'
    },
    {
      icon: '🍃',
      title: 'MongoDB Administration',
      description: 'NoSQL MongoDB database management and optimization'
    },
    {
      icon: '📊',
      title: 'Performance Optimization',
      description: 'Database performance tuning and query optimization'
    }
  ], []);

  const testimonials = useMemo(() => [
    {
      name: 'Sarah Johnson',
      company: 'TechCorp Solutions',
      rating: 5,
      text: 'Rmt-DBA transformed our database performance. Their remote monitoring caught issues before they became problems.'
    },
    {
      name: 'Michael Chen',
      company: 'DataFlow Industries',
      rating: 5,
      text: 'Outstanding MySQL optimization services. Our query response times improved by 300% after their tuning.'
    },
    {
      name: 'Emily Rodriguez',
      company: 'CloudFirst Systems',
      rating: 5,
      text: 'Their PostgreSQL expertise is unmatched. Professional, reliable, and always available when we need them.'
    },
    {
      name: 'David Thompson',
      company: 'Enterprise Analytics',
      rating: 5,
      text: 'Outstanding Oracle database administration. Their backup and recovery solutions are bulletproof!'
    }
  ], []);

  const typewriterTexts = useMemo(() => [
    'AI Agentic Solutions',
    'Autonomous IT Operations',
    'Intelligent Decision Making',
    'Professional IT Solutions',
    'Expert Cybersecurity',
    'Cloud Innovation',
    'Digital Transformation'
  ], []);

  // Auto-rotate services every 3 seconds
  useEffect(() => {
    const serviceInterval = setInterval(() => {
      setCurrentService((prev) => (prev + 1) % services.length);
    }, 3000);
    return () => clearInterval(serviceInterval);
  }, [services.length]);

  // Auto-rotate testimonials every 4 seconds
  useEffect(() => {
    const testimonialInterval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(testimonialInterval);
  }, [testimonials.length]);

  // Typewriter effect
  useEffect(() => {
    const currentText = typewriterTexts[textIndex];
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (typewriterText.length < currentText.length) {
          setTypewriterText(currentText.slice(0, typewriterText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        if (typewriterText.length > 0) {
          setTypewriterText(typewriterText.slice(0, -1));
        } else {
          setIsDeleting(false);
          setTextIndex((prev) => (prev + 1) % typewriterTexts.length);
        }
      }
    }, isDeleting ? 50 : 100);

    return () => clearTimeout(timeout);
  }, [typewriterText, isDeleting, textIndex, typewriterTexts]);

  return (
    <div className="home-container">
      {/* Top Services Slider */}
      <div className="top-services-slider">
        <div className="services-track">
          {[...services, ...services].map((service, index) => (
            <div key={index} className="service-slide">
              <span className="service-icon">{service.icon}</span>
              <span className="service-title">{service.title}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Main Banner */}
      <div className="banner-section">
        <div className="banner-grid">
          {/* Left Section - Main Content */}
          <div className="banner-left">
            <div className="hero-content">
              <h1 className="hero-title">
                Welcome to <span className="highlight">Rmt-DBA</span>
              </h1>
              <h2 className="hero-subtitle">
                <span className="typewriter">{typewriterText}</span>
                <span className="cursor">|</span>
              </h2>
              <p className="hero-description">
                Expert database administration services for MySQL, PostgreSQL, SQL Server, Oracle, and MongoDB. 
                24/7 monitoring, performance optimization, and comprehensive database management solutions.
                Professional remote DBA services to ensure your databases are secure, optimized, and highly available.
              </p>
              <div className="hero-buttons">
                <button className="btn btn-primary">Get Started</button>
                <button className="btn btn-secondary">Learn More</button>
              </div>
              <div className="hero-stats">
                <div className="stat">
                  <span className="stat-number">500+</span>
                  <span className="stat-label">Projects Completed</span>
                </div>
                <div className="stat">
                  <span className="stat-number">98%</span>
                  <span className="stat-label">Client Satisfaction</span>
                </div>
                <div className="stat">
                  <span className="stat-number">24/7</span>
                  <span className="stat-label">Support Available</span>
                </div>
              </div>
            </div>
          </div>

          {/* Center Section - Featured Service */}
          <div className="banner-center">
            <div className="featured-service">
              <div className="service-icon-large">
                {services[currentService].icon}
              </div>
              <h3>{services[currentService].title}</h3>
              <p>{services[currentService].description}</p>
              <div className="service-indicators">
                {services.map((_, index) => (
                  <span
                    key={index}
                    className={`indicator ${index === currentService ? 'active' : ''}`}
                    onClick={() => setCurrentService(index)}
                  ></span>
                ))}
              </div>
            </div>
          </div>

          {/* Right Section - Testimonials */}
          <div className="banner-right">
            <div className="testimonials-section">
              <h3>What Our Clients Say</h3>
              <div className="testimonial-card">
                <div className="testimonial-content">
                  <div className="stars">
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                      <span key={i} className="star">⭐</span>
                    ))}
                  </div>
                  <p className="testimonial-text">
                    "{testimonials[currentTestimonial].text}"
                  </p>
                  <div className="testimonial-author">
                    <strong>{testimonials[currentTestimonial].name}</strong>
                    <span>{testimonials[currentTestimonial].company}</span>
                  </div>
                </div>
              </div>
              <div className="testimonial-indicators">
                {testimonials.map((_, index) => (
                  <span
                    key={index}
                    className={`indicator ${index === currentTestimonial ? 'active' : ''}`}
                    onClick={() => setCurrentTestimonial(index)}
                  ></span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom CTA Section */}
      <div className="cta-section">
        <div className="cta-content">
          <h2>Ready to Transform Your Business?</h2>
          <p>Contact us today for a free consultation and discover how we can help you achieve your technology goals.</p>
          <button className="btn btn-cta">Start Your Journey</button>
        </div>
      </div>
    </div>
  );
};

export default HomeNew;
